import type { SubscriptionPlan } from '@planner/types'

export type PlanKey = SubscriptionPlan

export type FeatureKey =
  | 'menuConfiguration'
  | 'clientManagement'
  | 'employeeManagement'
  | 'availabilityCalendar'
  | 'reservations'
  | 'analyticsDashboard'
  | 'googleCalendar'
  | 'logoOnDocuments'
  | 'basicReports'
  | 'advancedReports'
  | 'pushNotifications'
  | 'excelPdfExport'
  | 'multipleUsers'
  | 'multiVenueManagement'

export type PlanConfig = {
  key: PlanKey
  pricePLN: number
  eventLimit: number
  features: Record<FeatureKey, boolean>
  /**
   * Order of features as shown on landing & plan selector.
   * This makes pricing UI the source of truth for what's advertised.
   */
  featureOrder: FeatureKey[]
}

export const FEATURE_ORDER_DEFAULT: FeatureKey[] = [
  'menuConfiguration',
  'clientManagement',
  'employeeManagement',
  'availabilityCalendar',
  'reservations',
  'analyticsDashboard',
  'googleCalendar',
  'logoOnDocuments',
  'basicReports',
  'advancedReports',
  'pushNotifications',
  'excelPdfExport',
  'multipleUsers',
  'multiVenueManagement',
]

export const planConfig: Record<PlanKey, PlanConfig> = {
  free: {
    key: 'free',
    pricePLN: 0,
    eventLimit: 1,
    features: {
      menuConfiguration: true,
      clientManagement: true,
      employeeManagement: true,
      availabilityCalendar: true,
      reservations: true,
      analyticsDashboard: true,
      googleCalendar: false,
      logoOnDocuments: false,
      basicReports: false,
      advancedReports: false,
      pushNotifications: false,
      excelPdfExport: false,
      multipleUsers: false,
      multiVenueManagement: false,
    },
    featureOrder: FEATURE_ORDER_DEFAULT,
  },
  starter: {
    key: 'starter',
    pricePLN: 199,
    eventLimit: 3,
    features: {
      menuConfiguration: true,
      clientManagement: true,
      employeeManagement: true,
      availabilityCalendar: true,
      reservations: true,
      analyticsDashboard: true,
      googleCalendar: true,
      logoOnDocuments: true,
      basicReports: true,
      advancedReports: false,
      pushNotifications: false,
      excelPdfExport: false,
      multipleUsers: false,
      multiVenueManagement: false,
    },
    featureOrder: FEATURE_ORDER_DEFAULT,
  },
  professional: {
    key: 'professional',
    pricePLN: 499,
    eventLimit: 10,
    features: {
      menuConfiguration: true,
      clientManagement: true,
      employeeManagement: true,
      availabilityCalendar: true,
      reservations: true,
      analyticsDashboard: true,
      googleCalendar: true,
      logoOnDocuments: true,
      basicReports: true,
      advancedReports: true,
      pushNotifications: true,
      excelPdfExport: false,
      multipleUsers: false,
      multiVenueManagement: false,
    },
    featureOrder: FEATURE_ORDER_DEFAULT,
  },
  enterprise: {
    key: 'enterprise',
    pricePLN: 999,
    eventLimit: 50,
    features: {
      menuConfiguration: true,
      clientManagement: true,
      employeeManagement: true,
      availabilityCalendar: true,
      reservations: true,
      analyticsDashboard: true,
      googleCalendar: true,
      logoOnDocuments: true,
      basicReports: true,
      advancedReports: true,
      pushNotifications: true,
      excelPdfExport: true,
      multipleUsers: true,
      multiVenueManagement: true,
    },
    featureOrder: FEATURE_ORDER_DEFAULT,
  },
}

export const planKeysInOrder: PlanKey[] = [
  'free',
  'starter',
  'professional',
  'enterprise',
]

export const paidPlanKeys: PlanKey[] = ['starter', 'professional', 'enterprise']

export function getPlanPricePLN(plan: PlanKey): number {
  return planConfig[plan].pricePLN
}

export function getPlanEventLimit(plan: PlanKey): number {
  return planConfig[plan].eventLimit
}

export function getPlanFeatures(plan: PlanKey): Record<FeatureKey, boolean> {
  return planConfig[plan].features
}

/**
 * Features that are marked as "coming soon" - not yet implemented
 */
export const COMING_SOON_FEATURES: FeatureKey[] = ['googleCalendar', 'multiVenueManagement']

/**
 * Checks if a feature is marked as "coming soon"
 */
export function isFeatureComingSoon(featureKey: FeatureKey): boolean {
  return COMING_SOON_FEATURES.includes(featureKey)
}

/**
 * Checks if a plan is equal to or higher than the required plan
 * @param currentPlan The current subscription plan
 * @param requiredPlan The minimum required plan
 * @returns true if currentPlan >= requiredPlan in the hierarchy
 */
export function isPlanEqualOrHigher(currentPlan: PlanKey, requiredPlan: PlanKey): boolean {
  const currentIndex = planKeysInOrder.indexOf(currentPlan)
  const requiredIndex = planKeysInOrder.indexOf(requiredPlan)
  
  // If either plan is not found, return false
  if (currentIndex === -1 || requiredIndex === -1) {
    return false
  }
  
  return currentIndex >= requiredIndex
}

